import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { userApi } from '@/common/services/api'
import { addNotification } from '@/store/notificationsSlice'
import { close } from '@/store/modalsSlice'
import { HYDRATE } from 'next-redux-wrapper'

export const fetchUserInfo = createAsyncThunk(
  'user/fetchUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const [user, subscription, referralBalance] = await Promise.all([
        userApi.getInfo(),
        userApi.getSubscription(),
        userApi.getReferralBalance(),
      ])

      return { user, subscription, referralBalance }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const deleteUserSocial = createAsyncThunk(
  'user/deleteUserSocial',
  async (data, { rejectWithValue, dispatch }) => {
    try {
      await userApi.removeUserSocial({
        headers: {
          'X-Language': data.locale,
        },
        data: {
          methodName: data.social,
        },
      })

      dispatch(close())
      dispatch(
        addNotification({
          type: 'success',
          text: `common.mainSuccess`,
        })
      )

      return data.social
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

const initialState = {
  user: null,
  subscription: null,
  referralBalance: null,
  userRedirectPath: null,
  isLimitSession: false,
  needShowUserLimitSession: false,
  seonToken: '',
  isVisibleAuthModal: false,

  error: null,
  status: null,
  isUserWithDefaultLanguage: false,
  isFirstRender: true,
  experiments: null,
}

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload
    },
    setSubscription: (state, action) => {
      state.subscription = action.payload
    },
    changeRecurrentSubscription: (state, action) => {
      state.subscription.is_recurrent = action.payload
    },
    setReferralBalance: (state, action) => {
      state.referralBalance = action.payload
    },
    setUserRedirectLoginPath: (state, { payload }) => {
      state.userRedirectPath = payload
    },
    setIsVisibleAuthModal(state, { payload }) {
      state.isVisibleAuthModal = payload
    },
    clear: (state) => {
      state.user = null
      state.subscription = null
      state.referralBalance = null
    },
    setUserInfo: (state, { payload }) => {
      state.isFetchUserInfo = payload
    },
    setLimitSession(state, { payload }) {
      state.isLimitSession = payload
    },
    setFirstRender(state, { payload }) {
      state.isFirstRender = payload
    },
    setExperiments(state, { payload }) {
      state.experiments = payload
    },
    setShowLimitSession(state, { payload }) {
      state.needShowUserLimitSession = payload
    },
    setSeonToken(state, { payload }) {
      state.seonToken = payload
    },
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      const { user } = action.payload
      state.isFirstRender = user.isFirstRender

      if (!state.experiments) {
        state.experiments = user.experiments
      }

      if (!user.isLimitSession) return state
      state.isLimitSession = user.isLimitSession
      state.needShowUserLimitSession = user.needShowUserLimitSession
    },
    [fetchUserInfo.pending]: (state) => {
      state.status = 'loading'
      state.error = null
    },
    [fetchUserInfo.fulfilled]: (state, action) => {
      state.status = 'resolved'
      state.user = action.payload.user
      state.subscription = action.payload.subscription
      state.referralBalance = action.payload.referralBalance
    },
    [fetchUserInfo.rejected]: (state, action) => {
      state.status = 'rejected'
      state.error = action.payload
    },
    [deleteUserSocial.fulfilled]: (state, { payload }) => {
      state.user.auth_methods[payload] = null
    },
  },
})

export const {
  setUser,
  setSubscription,
  setReferralBalance,
  clear,
  setUserRedirectLoginPath,
  setLimitSession,
  setFirstRender,
  setShowLimitSession,
  setSeonToken,
  setIsVisibleAuthModal,
  setExperiments,
  changeRecurrentSubscription,
} = userSlice.actions

export default userSlice.reducer
